// React
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// Hooks and Redux
import { useAppSelector } from "../../../redux/hooks";
import useDebounce from "../../../hooks/useDebounce";

// MUI Components
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import dayjs from "dayjs"

import {
  GridCellParams,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridValueGetterParams,
  GridRenderCellParams,
  DataGrid,
  enUS,
} from "@mui/x-data-grid"
// Utils
import match from "autosuggest-highlight/match"
import parse from "autosuggest-highlight/parse"
import { useTranslation } from "react-i18next"

// Types
import theme from "../../../styles/theme"
import { thTHGrid } from "../../../@types/DataGridLocaleText"
import { FilterOption } from "../../../@types"

// Services
import { getAllActiveOTP } from "../../../services/MasterDataService" //Change ALL
import React from "react"

const OTP = () => {
  const { t, i18n } = useTranslation()
  const userRoleType = useAppSelector((state) => state.user.roletype)
  const navigate = useNavigate()

  const [otpsLoading, setotpsLoading] = useState<boolean>(true)
  const [otpsData, setOTPSData] = useState<any[]>([])
  const [searchQuery, setSearchQuery] = useState<string>("")
  const [selectedStatus, setSelectedStatus] = useState<FilterOption>({
    label: `${t("All")}`,
    value: null,
  })
  const [isSearching, setIsSearching] = useState<boolean>(false)

  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  const allSuccessLoading = !otpsLoading

  useEffect(() => {
    const fetchotpss = async () => {
      try {
        setotpsLoading(true)

        const response = await getAllActiveOTP()
        const data = await response.data

        if (data.success) {
          setOTPSData(data.otps)
        } else {
          setOTPSData([])
        }
      } catch (error) {
        navigate("/servererror", { replace: true })
      } finally {
        setTimeout(() => {
          setotpsLoading(false)
          setIsSearching(false)
        }, 500)
      }
    }

    if (debouncedSearchQuery !== "") {
      setIsSearching(true)
      fetchotpss()
    } else {
      setSearchQuery("")
      fetchotpss()
    }
  }, [debouncedSearchQuery, navigate, selectedStatus])

  useEffect(() => {
    if (searchQuery !== "") {
      setIsSearching(true)
      setOTPSData([])
    }
  }, [searchQuery])

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
      </GridToolbarContainer>
    )
  }

  const columns: GridColDef[] = [
    {
      field: "mobile_number",
      headerName: `${t("otp.mobileNumber")}`,
      width: 150,
      valueGetter: (params: GridCellParams) => params.row.mobile_number,
    },
    {
      field: "value",
      headerName: `${t("otp.value")}`,
      width: 100,
      valueGetter: (params: GridCellParams) => params.row.value,
    },
    {
      field: "verified",
      headerName: `${t("otp.verified")}`,
      width: 120,
      hideSortIcons: true,
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams) =>
        params.row.verified === true ? (
          <Chip
            variant="outlined"
            color="success"
            label={`${t("otp.text_verified")}`}
            size="small"
          />
        ) : (
          <Chip
            variant="outlined"
            color="error"
            label={`${t("otp.text_unverified")}`}
            size="small"
          />
        ),
    },
    {
      field: "smid",
      headerName: `${t("otp.smid")}`,
      width: 200,
      valueGetter: (params: GridCellParams) => params.row.smid,
    },
    {
      field: "delivery_report",
      headerName: `${t("otp.deliveryReport")}`,
      width: 200,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.delivery_report?.deliveryStatus ?? "-",
      renderCell: (params: GridRenderCellParams) => {
        const report = params.row.delivery_report

        // กรณี null
        if (!report) {
          return <span style={{ color: "#999" }}>-</span>
        }
        const isSuccess = report.deliveryStatus === "Success"

        return (
          <Tooltip
            title={
              <div>
                <div>Detail: {report.deliveryDetail}</div>
                <div>Status: {report.deliveryStatus}</div>
                <div>Dest: {report.destinationNumber}</div>
                <div>Sender: {report.senderName}</div>
              </div>
            }
          >
            <Chip
              label={report.deliveryDetail || report.deliveryStatus}
              color={isSuccess ? "success" : "error"}
              size="small"
            />
          </Tooltip>
        )
      },
    },
    {
      field: "created_date",
      headerName: `${t("otp.createdDate")}`,
      width: 150,
      renderCell: (params: GridCellParams) => {
        let created_date = dayjs(params.row.created_date).format(
          i18n.language === "en" ? "DD/MM/YYYY HH:mm" : "DD/MM/BBBB HH:mm"
        )

        return <Typography sx={{ fontSize: 14 }}>{created_date}</Typography>
      },
    },
  ]

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "70px",
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: "bold", textAlign: "left" }}>
          OTP
        </Typography>
        {/* {
                    userRoleType === 'Super Admin' && (
                        <Button
                            variant="contained"
                            disableElevation
                            sx={{
                                backgroundColor: theme.palette.button.main,
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: '#0288d1',
                                },
                                minWidth: 'fit-content',
                                minHeight: '42px',
                            }}
                            component={Link}
                            to="/admin/masterdata/otpss/create"
                        >
                            {t("Create New otps")}
                        </Button>
                    )
                } */}
      </Box>

      <Box sx={{ width: "100%", paddingX: 3, mt: 3 }}>
        {isSearching && allSuccessLoading && (
          <Typography align="center">{t("Searching")}...</Typography>
        )}
        {!allSuccessLoading ? (
          <Box display="flex" justifyContent="center" gap={2}>
            <CircularProgress color="success" size={20} />
            <Typography align="center">{t("Loading")}...</Typography>
          </Box>
        ) : otpsData.length !== 0 ? (
          <Box display="inline-grid" sx={{ width: "100%" }}>
            <DataGrid
              getRowId={(row) => row.id}
              rows={otpsData}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 },
                },
              }}
              pagination
              pageSizeOptions={[10, 20, 30, 40, 50]}
              getRowHeight={() => 50}
              disableRowSelectionOnClick
              sx={{
                "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus":
                  {
                    outline: "none !important",
                  },
                fontSize: 13,
              }}
              slots={{
                toolbar: CustomToolbar,
              }}
              slotProps={{
                filterPanel: {
                  filterFormProps: {
                    logicOperatorInputProps: {
                      variant: "outlined",
                      size: "small",
                    },
                    columnInputProps: {
                      variant: "outlined",
                      size: "small",
                      sx: { mt: "auto" },
                    },
                    operatorInputProps: {
                      variant: "outlined",
                      size: "small",
                      sx: { mt: "auto" },
                    },
                    valueInputProps: {
                      InputComponentProps: {
                        variant: "outlined",
                        size: "small",
                      },
                    },
                    deleteIconProps: {
                      sx: {
                        "& .MuiSvgIcon-root": { color: "#d32f2f" },
                      },
                    },
                  },
                  sx: {
                    "& .MuiDataGrid-filterForm": { p: 2 },
                    "& .MuiDataGrid-filterForm:nth-of-type(even)": {
                      backgroundColor: () =>
                        theme.palette.mode === "dark" ? "#444" : "#f5f5f5",
                    },
                    "& .MuiDataGrid-filterFormLogicOperatorInput": { mr: 2 },
                    "& .MuiDataGrid-filterFormColumnInput": {
                      mr: 2,
                      width: 150,
                    },
                    "& .MuiDataGrid-filterFormOperatorInput": { mr: 2 },
                    "& .MuiDataGrid-filterFormValueInput": { width: 200 },
                  },
                },
              }}
              localeText={
                i18n.language === "en"
                  ? enUS.components.MuiDataGrid.defaultProps.localeText
                  : thTHGrid
              }
              // unstable_headerFilters
            />
          </Box>
        ) : !isSearching ? (
          <Typography align="center">{t("No data")}</Typography>
        ) : null}
      </Box>
    </>
  )
}

export default OTP;
